<template>
  <div class="as-content-page">
    <filter-tags
      :tags="tags"
      :leagues="leagues"
      :sortBy="sortBy"
      :filter="filter"
      @changeTag="(newVal) => changeTag(newVal)"
      @changeLeague="(newVal) => changeLeague(newVal)"
      @sorByTime="(newVal) => sorByTime(newVal)"
    />

    <div v-if="isLoading" class="as-py-24">
      <MatchsSkeleton />
    </div>
    <MatcheList
      v-else-if="competitions.length > 0"
      :competitions="competitions"
      :filter="filter"
      :competitionType="competitionType"
      :matchLives="matchLives"
    />
    <NoData v-else />
  </div>
</template>

<script lang="ts">
import { defineComponent, toRaw } from "vue";
import { addDays, format } from "date-fns";
import FilterTags from "~src/components/FilterTags.vue";
import MatcheList from "~src/modules/schedule/components/MatcheList.vue";
import MatchsSkeleton from "~src/components/skeleton/schedule/MatchsSkeleton.vue";
import NoData from "~src/components/NoData.vue";
import { formatLocalTime } from "~src/helpers/CommonHelper";
import { parseMatchData, parseCompetitionData } from "~src/helpers";
import {
  competitionFeatured,
  matchSchedule,
  scheduleBySeasonId,
} from "~src/services/SpapiService";

import { getMatchLive } from "~src/services/RemoteLiveService";

import MatchEnum from "~src/enums/MatchEnum";
import CompetitionEnum from "~src/enums/CompetitionEnum";
import type {
  IItemString,
  IItemNumber,
  ICompetitions,
  ICompetition,
  IMatch,
  IFilterResult,
  ITag,
  IMatchLiveData
} from "~src/interfaces";

export default defineComponent({
  name: "resultHome",
  components: {
    FilterTags,
    MatcheList,
    MatchsSkeleton,
    NoData,
  },
  props: {
    competitionId: {
      type: String,
    },
    seasonId: {
      type: String,
    },
  },
  data() {
    const tags: Array<ITag> = [];
    for (let i = 0; i < 8; i++) {
      tags.push({
        title: i === 0 ? "Hôm nay" : format(addDays(new Date(), i), "dd/MM"),
        value: format(addDays(new Date(), i), "yyyy-MM-dd"),
      });
    }

    return {
      tags,
      leagues: [] as Array<ICompetition>,
      matchs: [] as Array<IMatch>,
      competitions: [] as Array<ICompetitions>,
      matchLives: {} as IMatchLiveData,
      isLoading: false,
      sortBy: "league",
      competitionType: 0,
      filter: {
        tag: tags[0],
        league: null,
      } as IFilterResult,
    };
  },
  async created() {
    const [rsMatchLives, rsCompetition] = await Promise.all([
      getMatchLive(null).catch(e => {
        console.error("Error in getMatchLive:", e);
        return null; // hoặc xử lý fallback tại đây
      }),
      competitionFeatured().catch(e => {
        console.error("Error in competitionFeatured:", e);
        return null; // hoặc xử lý fallback tại đây
      })
    ]);

    if(rsMatchLives) {
      this.matchLives = rsMatchLives.data;
    }
    if(rsCompetition) {
      this.leagues = rsCompetition.data.data;
    }

    if (this.seasonId) {
      const league = this.leagues.find(
        (item) => item.cur_season_id === this.seasonId
      );
      if (league) {
        this.filter = {
          ...this.filter,
          tag: null,
          league,
        } as IFilterResult;

        await this.getScheduleBySeasonId(league.cur_season_id);
      } else {
        await this.getMatch();
      }
    } else if (this.competitionId) {
      const league = this.leagues.find(
        (item) => item.id === this.competitionId
      );
      if (league) {
        this.filter = {
          ...this.filter,
          tag: null,
          league,
        } as IFilterResult;

        await this.getScheduleBySeasonId(league.cur_season_id);
      } else {
        await this.getMatch();
      }
    } else {
      await this.getMatch();
    }
  },
  methods: {
    async formatMatch() {
      let matchs = toRaw(JSON.parse(JSON.stringify(this.matchs)));
      let competitions = toRaw(JSON.parse(JSON.stringify(this.competitions)));

      if (this.filter.tag) {
        if (this.sortBy === "time") {
          matchs.sort((a: IMatch, b: IMatch) => a.match_time - b.match_time);
        } else {
          const dataSort = [];
          for (const league of this.leagues) {
            const ms = matchs.filter(
              (item: IMatch) => item.competition_id === league.id
            );
            dataSort.push(...ms);
          }
          for (const match of matchs) {
            const checkNot = this.leagues.find(
              (item) => item.id === match.competition_id
            );
            if (!checkNot) {
              dataSort.push(match);
            }
          }
          matchs = dataSort;
        }
      } else {
        matchs.sort((a: IMatch, b: IMatch) => a.match_time - b.match_time);
      }

      const datas: ICompetitions[] = [];
      for (const match of matchs) {
        if (match.status_id !== MatchEnum.STATUS_NOT_STARTED) {
          continue;
        }
        const competition = competitions
          ? competitions.find(
              (item: ICompetition) => item.id === match.competition_id
            )
          : match.competition;

        if (!competition) {
          continue;
        }

        if (this.filter.tag) {
          const competitionId = competition.id;
          if (this.sortBy === "time") {
            if (datas.length) {
              if (datas[datas.length - 1].id === match.competition_id) {
                datas[datas.length - 1].matchs.push(match);
              } else {
                datas.push({
                  ...competition,
                  title: competition.short_name_vi
                    ? competition.short_name_vi
                    : competition.short_name,
                  isShowLogo: true,
                  matchs: [match],
                });
              }
            } else {
              datas.push({
                ...competition,
                matchs: [match],
              });
            }
          } else {
            const index = datas.findIndex(
              (item: ICompetitions) => item.id === match.competition_id
            );
            if (index !== -1) {
              datas[index].matchs.push(match);
            } else {
              datas.push({
                ...competition,
                title: competition.short_name_vi
                  ? competition.short_name_vi
                  : competition.short_name,
                isShowLogo: true,
                matchs: [match],
              });
            }
          }
        } else {
          const date = formatLocalTime(match.match_time);
          const index = datas.findIndex(
            (item: ICompetitions) => item.date === date
          );
          if (index === -1) {
            const dateData = new Date(match.match_time * 1000);
            const weekdays: IItemNumber = {
              0: "Chủ nhật",
              1: "Thứ hai",
              2: "Thứ ba",
              3: "Thứ tư",
              4: "Thứ năm",
              5: "Thứ sáu",
              6: "Thứ bảy",
            };
            const weekday = weekdays[dateData.getDay()];

            const title = `${weekday}, Ngày ${date}`;

            datas.push({
              ...competition,
              date,
              title,
              isShowLogo: false,
              matchs: [match],
            });
          } else {
            datas[index].matchs.push(match);
          }
        }
      }

      this.competitions = datas as Array<ICompetitions>;
    },
    async getMatch(date = null) {
      this.isLoading = true;
      const rs = await matchSchedule(date ? date : this.tags[0].value);
      this.matchs = parseMatchData(rs.data.data.matches);
      this.competitions = parseCompetitionData(rs.data.data.competitions);

      await this.formatMatch();
      this.isLoading = false;
    },
    async getScheduleBySeasonId(seasonId: string) {
      this.competitionType = 0;
      this.isLoading = true;
      const rs = await scheduleBySeasonId(seasonId);
      // const matchs = decryptData(rs.data.data);
      const matchs = rs.data.data.matches;
      const competitions = rs.data.data.competitions;
      if (matchs.length) {
        this.competitionType = competitions[0]?.type;
      }
      this.matchs = matchs;
      this.competitions = competitions;
      await this.formatMatch();
      this.isLoading = false;
    },
    async changeTag(newVal: any) {
      this.filter = {
        tag: newVal,
        league: null,
      } as IFilterResult;
      await this.getMatch(newVal.value);
    },
    async changeLeague(newVal: any) {
      this.filter = {
        tag: null,
        league: newVal,
      } as IFilterResult;
      await this.getScheduleBySeasonId(newVal.cur_season_id);
    },
    async sorByTime(newVal: string) {
      this.sortBy = newVal;
      this.isLoading = true;
      await this.formatMatch();
      this.isLoading = false;
    },
  },
});
</script>

<style scoped></style>
